import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/ReviewSession.module.css";
import { X, ArrowUp, PhoneOff } from "lucide-react";
import SessionActions from "./SessionActions";
import { reviewChat, startReviewChat } from "../../requests";
import ResponseLoader from "../common/ResponseLoader";
import { deleteAuthToken } from "../../utils/helper";
import { useSelector } from "react-redux";
import { isHTML, formatHTMLResponse } from "../helper/formatters";

const AI_AVATAR = `${process.env.PUBLIC_URL}/img/user-icon.png`;
const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const summaryMessages = [
  {
    sender: "ai",
    text: "A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.",
    summary: true,
  },
];

const ReviewSession = ({ show, onClose, onEndSession, sessionId = null }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [showSaveSession, setShowSaveSession] = useState(false);
  const [summary, setSummary] = useState(false);
  const [timer, setTimer] = useState("45:00");
  const [chatId, setChatId] = useState(null);

  const { reviewSessionTitle } = useSelector((state) => state.reviewSession);

  const inputRef = useRef(null);
  const chatEndRef = useRef(null);

  // Countdown timer effect
  useEffect(() => {
    if (!show) return;

    // Convert MM:SS format to total seconds
    const [minutes, seconds] = timer.split(":").map(Number);
    let totalSeconds = minutes * 60 + seconds;

    const interval = setInterval(() => {
      if (totalSeconds <= 0) {
        clearInterval(interval);
        onClose?.(); // Call onClose when countdown reaches 00:00
        return;
      }

      totalSeconds--;

      // Convert back to MM:SS format
      const mins = Math.floor(totalSeconds / 60);
      const secs = totalSeconds % 60;
      const newTimer = `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;

      setTimer(newTimer);
    }, 1000);

    // Cleanup interval on unmount or when show changes
    return () => clearInterval(interval);
  }, [show, onClose]);

  React.useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [messages]);

  React.useEffect(() => {
    if (show) {
      handleStartChat();
    } else {
      resetStates();
    }
    return () => {
      resetStates();
    };
  }, [show]);

  const resetStates = () => {
    setMessages([]);
    setInput("");
    setWaiting(false);
    setHovered("");
    setShowSaveSession(false);
    setTimer("45:00"); // Reset timer to initial value
  };

  const handleStartChat = async () => {
    setWaiting(true);
    try {
      const response = await startReviewChat({
        sessionId,
      });
      // Trigger a refresh of the current page to get updated data
      const aiText = response.data.message;
      const chatId = response.data.chat_id;
      setMessages([{ sender: "ai", text: aiText }]);
      setInput("");
      setChatId(chatId);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
    }
    setWaiting(false);
  };

  const handleChat = async (userMessage) => {
    if (!messages.length || !chatId) return;
    try {
      const response = await reviewChat({
        chat_id: chatId,
        user_msg: userMessage,
      });
      const aiText = response.data.message;

      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    // to regenerate summary
    if (summary) {
      setSummary(false);
    }
    setMessages((msgs) => [...msgs, { sender: "Admin", text: input }]);
    setInput("");
    setWaiting(true);
    const aiText = await handleChat(input);
    setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    setWaiting(false);
  };

  const handleGenerateSummary = () => {
    setInput("");
    setWaiting(true);
    setTimeout(() => {
      setMessages((msgs) => [...msgs, ...summaryMessages]);
      setWaiting(false);
      setTimeout(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }, 1500 + Math.random() * 1000);
    setSummary(true);
    setShowSaveSession(false);
  };

  return (
    <>
      {show && (
        <div className={styles.overlay}>
          <div className={styles.popup}>
            <div className={styles.header}>
              <h4 className={styles.title}>
                {reviewSessionTitle}
              </h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div className={styles.chatBody}>
              {messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={
                    msg.sender === "Admin" ? styles.drMsg : styles.aiMsg
                  }
                >
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      {msg.sender !== "Admin" && (
                        <i>
                          <img src={DR_AVATAR} alt="Dr. Salah" />
                        </i>
                      )}

                      <span>{msg.sender === "Admin" ? "Admin" : ""}</span>
                    </div>
                    {msg?.summary && <strong>Summary Generated:</strong>}
                    {isHTML(msg.text) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatHTMLResponse(msg.text),
                        }}
                      />
                    ) : (
                      <p className="m-0 mt-2">{msg.text}</p>
                    )}
                  </div>
                </div>
              ))}
              {waiting && (
                <div className={styles.aiMsg}>
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img src={DR_AVATAR} alt="Dr. Salah" />
                      </i>
                    </div>

                    <ResponseLoader />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <hr className="m-0" />
            <div className={styles.inputRow}>
              <input
                ref={inputRef}
                className={styles.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !waiting) handleSend();
                }}
                placeholder="Talk to Dr. Salah...."
                disabled={waiting}
              />
              <div className={styles.bottomBar}>
                <span className={styles.bottomIconTime}>{timer}</span>

                <div
                  onClick={() => {
                    if (waiting || !input.trim()) return;
                    handleSend();
                  }}
                  className={
                    styles.bottomIconWrap +
                    (waiting || !input.trim() ? " " + styles.disabled : "")
                  }
                >
                  <ArrowUp className={styles.bottomIcon} />
                </div>

                <div
                  className={styles.bottomIconWrapDanger}
                  onMouseEnter={() => setHovered("endSession")}
                  onMouseLeave={() => setHovered("")}
                  onClick={onEndSession}
                >
                  <PhoneOff className={styles.bottomIcon} />
                  <span
                    className={
                      hovered === "endSession"
                        ? styles.bottomTextShow
                        : styles.bottomTextHide
                    }
                  >
                    End Session
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showSaveSession &&
        createPortal(
          <SessionActions
            show={showSaveSession}
            onHide={() => setShowSaveSession(false)}
            generateSummary={handleGenerateSummary}
            saveSession={() => {}}
            summary={summary}
          />,
          document.body
        )}
    </>
  );
};

export default ReviewSession;
